import React from 'react';
import { Star, MapPin, Zap, CheckCircle2, Navigation } from 'lucide-react';
import { Shop, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface ShopCardProps {
  shop: Shop;
  onClick: (shop: Shop) => void;
  distance?: number;
}

export const ShopCard: React.FC<ShopCardProps> = ({ shop, onClick, distance }) => {
  const { language } = useTranslation();
  const isCurrentlyOpen = () => {
    const hour = new Date().getHours();
    return hour >= 8 && hour < 18;
  };

  const isOpen = isCurrentlyOpen();

  const labels: Record<string, Record<Language, string>> = {
    open: { EN: 'Open', KH: 'កំពុងបើក', ZH: '营业中', JA: '営業中', KO: '영업 중' },
    closed: { EN: 'Closed', KH: 'បានបិទ', ZH: '休息中', JA: '準備中', KO: '영업 종료' },
    book: { EN: 'Book Repair', KH: 'កក់ជួសជុល', ZH: '预约维修', JA: '修理予約', KO: '수리 예약' }
  };

  return (
    <div 
      onClick={() => onClick(shop)}
      className="group bg-white rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-violet-200/20 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col h-full"
    >
      <div className="relative h-48 overflow-hidden bg-slate-100">
        <img 
          src={shop.imageUrl} 
          alt={shop.name} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
        
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-2 py-1 rounded-xl flex items-center shadow-lg border border-white/20">
          <Star size={12} className="text-amber-500 fill-amber-500 mr-1" />
          <span className="text-[11px] font-black text-slate-900">{shop.rating}</span>
        </div>

        <div className="absolute top-4 left-4">
           <div className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center shadow-md backdrop-blur-md border border-white/20 ${
             isOpen ? 'bg-emerald-500/90 text-white' : 'bg-slate-800/90 text-slate-300'
           }`}>
             <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isOpen ? 'bg-white animate-pulse' : 'bg-slate-400'}`} />
             {isOpen ? labels.open[language] : labels.closed[language]}
           </div>
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-3">
           <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <h3 className="text-lg font-black text-slate-900 truncate tracking-tight">{shop.name}</h3>
                {shop.rating >= 4.5 && <CheckCircle2 size={14} className="text-blue-500" fill="currentColor" />}
              </div>
              <div className="flex items-center text-slate-400 text-xs font-bold">
                <MapPin size={12} className="mr-1 flex-shrink-0" />
                <span className="truncate">{shop.address.split(',')[1] || shop.address}</span>
              </div>
           </div>
           
           {distance !== undefined && (
             <div className="text-[10px] font-black text-violet-600 bg-violet-50 px-2 py-1 rounded-lg border border-violet-100 flex-shrink-0 flex items-center">
               <Navigation size={10} className="mr-1" />
               {distance.toFixed(1)}km
             </div>
           )}
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-5">
           {shop.category.slice(0, 2).map((cat, idx) => (
              <span key={idx} className="bg-slate-50 text-slate-500 text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider border border-slate-100">
                {cat}
              </span>
           ))}
           {shop.category.length > 2 && <span className="text-[9px] font-black text-slate-300">+{shop.category.length - 2}</span>}
        </div>

        <div className="mt-auto flex items-center justify-between">
           <div className="flex -space-x-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-slate-100 overflow-hidden shadow-sm">
                   <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${shop.id}-${i}`} alt="user" />
                </div>
              ))}
           </div>
           <div className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5 group-hover:text-violet-600 transition-colors">
             {labels.book[language]} <Zap size={10} className="fill-current" />
           </div>
        </div>
      </div>
    </div>
  );
};
